import type { CSSProperties } from 'react';
import type { IndicatorResultNode } from '@/api/assessment';
import { IndicatorRow } from './IndicatorRow';

/**
 * 10 项指标列表 — 处理 OR 复合 children 合并行。
 *
 * 后端 results 已扁平排序（assess.py:60 sort_order），含 children 字段时为 OR 复合父项。
 * 父行 passed = any(children.passed) 由后端算（assess.py:78），前端只渲染。
 *
 * 条款编号映射：按 top-level 顺序套中文一二三...（与原型 ops-compliance-dashboard.html 对位）。
 * 实际条款序号由后端 rule.sort_order 决定，前端只做视觉编号。
 */

export interface IndicatorListProps {
  results: IndicatorResultNode[];
}

// 中文一~十；超出 10 项回退到数字（防 ruleset 扩展）
const CLAUSE_LABELS = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
function clauseLabel(idx: number): string {
  return CLAUSE_LABELS[idx] ?? String(idx + 1);
}

const headerStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '48px 1.6fr 92px 1.5fr 84px',
  gap: 10,
  padding: '9px 14px',
  background: 'var(--color-bg-page-warm)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  fontWeight: 'var(--font-weight-medium)' as CSSProperties['fontWeight'],
};

export function IndicatorList({ results }: IndicatorListProps) {
  return (
    <div
      style={{
        border: '1px solid var(--color-line)',
        borderRadius: 'var(--radius-xl)',
        overflow: 'hidden',
        background: 'var(--color-bg-card)',
      }}
    >
      <div style={headerStyle}>
        <span style={{ textAlign: 'center' }}>条款</span>
        <span>评价指标</span>
        <span>要求</span>
        <span>当前值</span>
        <span style={{ textAlign: 'right' }}>状态</span>
      </div>

      {results.map((node, idx) => {
        const hasChildren = node.children && node.children.length > 0;
        const clause = clauseLabel(idx);
        return (
          <div key={node.code}>
            <IndicatorRow
              data={node}
              clause={clause}
              orParent={hasChildren}
            />
            {hasChildren &&
              node.children.map((child) => (
                <IndicatorRow key={child.code} data={child} sub />
              ))}
          </div>
        );
      })}
    </div>
  );
}
